import { useCallback, useMemo, useState } from "react";
import { DirectorySort } from "@common/constants/counterparty";
import { CounterpartyCreationResponse } from "@common/contracts/counterparty.contract";
import { RegisteredCounterpartyIncludeScope } from "@common/helpers/counterparties";
import { useCounterpartiesQuery } from "@api/counterparty.api";
import { InjectedCommandOption } from "@components/commands/lib";

interface UseInvoiceBulkCounterpartyOptionsParams {
    onSelectCounterparty: (counterpartyId: string) => void;
}

export function useInvoiceBulkCounterpartyOptions({
    onSelectCounterparty,
}: UseInvoiceBulkCounterpartyOptionsParams) {
    const [isCreateCounterpartyOpen, setIsCreateCounterpartyOpen] =
        useState(false);
    const { data: counterpartiesResponse } = useCounterpartiesQuery({
        sort: DirectorySort.NAME_ASC,
        includeRegistered: RegisteredCounterpartyIncludeScope.USED,
    });

    const counterpartyOptions: InjectedCommandOption[] = useMemo(() => {
        const counterparties = (counterpartiesResponse?.data ?? [])
            .map((row) => row.counterparty)
            .filter(
                (
                    counterparty,
                ): counterparty is NonNullable<typeof counterparty> =>
                    counterparty != null && counterparty.name.length > 0,
            );

        const existingOptions: InjectedCommandOption[] = counterparties.map(
            (counterparty) => ({
                id: `counterparty-${counterparty.id}`,
                label: counterparty.name,
                keywords: [counterparty.name],
                onSelect: () => {
                    void onSelectCounterparty(counterparty.id);
                },
            }),
        );

        return [
            {
                id: "counterparty-create-new",
                label: "Create new counterparty",
                keywords: ["create", "new", "add"],
                onSelect: () => setIsCreateCounterpartyOpen(true),
            },
            ...existingOptions,
        ];
    }, [counterpartiesResponse?.data, onSelectCounterparty]);

    const closeCreateCounterparty = useCallback(() => {
        setIsCreateCounterpartyOpen(false);
    }, []);

    const handleCreateCounterpartyClosed = useCallback(
        (response?: CounterpartyCreationResponse) => {
            setIsCreateCounterpartyOpen(false);
            if (!response) {
                return;
            }
            void onSelectCounterparty(response.counterparty.id);
        },
        [onSelectCounterparty],
    );

    return {
        counterpartyOptions,
        isCreateCounterpartyOpen,
        closeCreateCounterparty,
        handleCreateCounterpartyClosed,
    };
}
